import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Кастомный хук для обработки pull-to-refresh функциональности
 * @param {Function} onRefresh - Функция, которая будет вызвана при обновлении
 * @param {Object} options - Опции конфигурации
 * @returns {Object} - Объект с состоянием и методами для pull-to-refresh
 */
export const usePullToRefresh = (onRefresh, options = {}) => {
  const {
    threshold = 80, // Минимальное расстояние для активации обновления
    resistance = 2.5, // Сопротивление при перетаскивании
    maxPullDistance = 120, // Максимальное расстояние перетаскивания
    enabled = true // Включена ли функциональность
  } = options;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  
  const startY = useRef(0);
  const currentY = useRef(0);
  const elementRef = useRef(null);
  const isAtTop = useRef(false);

  // Проверяем, находимся ли мы в верхней части страницы
  const checkIfAtTop = useCallback(() => {
    if (!elementRef.current) return false;
    const scrollTop = elementRef.current.scrollTop || window.pageYOffset;
    return scrollTop <= 0;
  }, []);

  // Обработчик начала касания
  const handleTouchStart = useCallback((e) => {
    if (!enabled || isRefreshing) return;
    
    isAtTop.current = checkIfAtTop();
    if (!isAtTop.current) return;

    startY.current = e.touches[0].clientY;
    currentY.current = e.touches[0].clientY;
  }, [enabled, isRefreshing, checkIfAtTop]);

  // Обработчик движения касания
  const handleTouchMove = useCallback((e) => {
    if (!enabled || isRefreshing || !isAtTop.current) return;

    currentY.current = e.touches[0].clientY;
    const deltaY = currentY.current - startY.current;

    // Если тянем вниз (положительное значение)
    if (deltaY > 0) {
      e.preventDefault();
      
      const distance = deltaY;
      const resistanceDistance = distance / resistance;
      const limitedDistance = Math.min(resistanceDistance, maxPullDistance);
      
      setPullDistance(limitedDistance);
      setIsPulling(true);
    }
  }, [enabled, isRefreshing, resistance, maxPullDistance]);

  // Обработчик окончания касания
  const handleTouchEnd = useCallback(() => {
    if (!enabled || isRefreshing || !isPulling) return;

    if (pullDistance >= threshold) {
      // Активируем обновление
      setIsRefreshing(true);
      setPullDistance(0);
      setIsPulling(false);
      
      // Вызываем функцию обновления
      onRefresh().finally(() => {
        setIsRefreshing(false);
      });
    } else {
      // Возвращаем в исходное состояние
      setPullDistance(0);
      setIsPulling(false);
    }
  }, [enabled, isRefreshing, isPulling, pullDistance, threshold, onRefresh]);

  // Добавляем обработчики событий
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled) return;

    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Функция для программного запуска обновления
  const triggerRefresh = useCallback(() => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    onRefresh().finally(() => {
      setIsRefreshing(false);
    });
  }, [isRefreshing, onRefresh]);

  return {
    elementRef,
    isRefreshing,
    isPulling,
    pullDistance,
    triggerRefresh,
    // Вычисляемые значения для UI
    shouldShowRefreshIndicator: isPulling || isRefreshing,
    refreshProgress: Math.min(pullDistance / threshold, 1),
    canRefresh: pullDistance >= threshold
  };
};
